import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { GET } from "@/services/activity-requests";
import { toast } from "sonner";
import { tokenRefresh } from "@/redux/Refresh";
import { removeMultipleKeys } from "@/utils/function";

interface ActivityLevelData {
  [key: string]: any;
}

interface ActivityLevelState {
  data: ActivityLevelData | null;
  loading: boolean;
  error: string | null;
}

interface FetchActivityLevelArgs {
  token: string;
  language: string;
}

const initialState: ActivityLevelState = {
  data: null,
  loading: false,
  error: null,
};

export const activityLevelFetch = createAsyncThunk<
  ActivityLevelData,
  FetchActivityLevelArgs,
  { rejectValue: string }
>("activity-levels", async ({ token, language }, { rejectWithValue }) => {
  try {
    const { response } = await GET(`/interactive-lessons/${language}/levels`, token);
    const result = response?.data?.result;

    return result;
  } catch (err: unknown) {
    const error = err as any;
    if (error.response?.status === 401) {
      removeMultipleKeys(["pluri_token", "pluri_refresh_token", "user_detail"]);
      toast.error("Session expired. Please log in again.", {
        id: "session-expired-toast",
        richColors: true,
      });
      window.location.href = "/login";
    }
    const errorMessage = error.response?.data?.message || "An error occurred";
    return rejectWithValue(errorMessage);
  }
});

const activityLevelSlice = createSlice({
  name: "activityLevel",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(activityLevelFetch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        activityLevelFetch.fulfilled,
        (state, action: PayloadAction<ActivityLevelData>) => {
          state.data = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(
        activityLevelFetch.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Unknown error";
        }
      );
  },
});

export default activityLevelSlice.reducer;
